import { DndContext, closestCenter } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  CellContext,
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/shadcn/button'
import { Table } from '@/components/shadcn/table'
import { TooltipButton } from '@/components/ui/TooltipButton'

import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { FormProvider } from 'react-hook-form'
import { FieldRenderer } from '../FormComponent/FieldRenderer'
import { useSubmitForm } from '../FormComponent/useSubmitForm'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { SearchInput } from '../shared/SearchInput'

import { IconV2RotateClockwiseOutline } from '@icons/IconV2RotateClockwiseOutline'
import clsx from 'clsx'
import { CompactViewToggleV2 } from './CompactViewToggleV2'
import { DataTableBodyV2 } from './components/DataTableBodyV2'
import { DataTableHeaderV2 } from './components/DataTableHeaderV2'
import { ExportButtonV2 } from './components/ExportButtonV2'
import { GroupingActionsV2 } from './components/GroupingActionsV2'
import { InlineEditActionsV2 } from './components/InlineEditActionsV2'
import { TableActionsV2 } from './components/TableActionsV2'
import { TableDragOverlayV2 } from './components/TableDragOverlayV2'
import { getColumnsV2 } from './getColumnsV2'
import { useColumnWidthsV2 } from './hooks/useColumnWidthsV2'
import { useExpanderColumnV2 } from './hooks/useExpanderColumnV2'
import { useFrozenColumnsV2 } from './hooks/useFrozenColumnsV2'
import { useInlineEditV2 } from './hooks/useInlineEditV2'
import { useTableColumnsV2 } from './hooks/useTableColumnsV2'
import { useTableDataGroupingV2 } from './hooks/useTableDataGroupingV2'
import { useTableDragDropV2 } from './hooks/useTableDragDropV2'
import { useTableRegroupingV2 } from './hooks/useTableRegroupingV2'
import { DataTableV2DataRow, DataTableV2DataSchema, DataTableV2UISchema } from './types'
import { isEmptyObject } from './utils'

// Threshold for enabling virtualization - only virtualize if we have more than this many rows
const VIRTUALIZATION_THRESHOLD = 100

function coerceBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }
  return fallback
}

/**
 * Conditionally wraps children in a FormProvider when inline editing is active.
 * This is required because shadcn form components (FormLabel, FormDescription, etc.)
 * use useFormContext() which needs a FormProvider ancestor.
 */
function InlineEditFormWrapper({
  isInlineEditing,
  formMethods,
  children,
}: {
  isInlineEditing: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formMethods: any
  children: React.ReactNode
}) {
  if (!isInlineEditing) return <>{children}</>
  return <FormProvider {...formMethods}>{children}</FormProvider>
}

interface DataTableV2ContentProps {
  uiSchema: DataTableV2UISchema
  dataSchema: DataTableV2DataSchema
  tileFilters?: React.ReactNode
  reloadHandler?: () => void
  isRefetching?: boolean
}

export function DataTableV2Content({
  uiSchema,
  dataSchema,
  tileFilters,
  reloadHandler,
  isRefetching = false,
}: DataTableV2ContentProps) {
  // Refs
  const tableBodyRef = useRef<HTMLTableSectionElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const headerMeasureRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const cellMeasureRefs = useRef<Record<string, HTMLDivElement[]>>({})
  const overlayCellMeasureRefs = useRef<Record<string, HTMLDivElement[]>>({})
  const tableRef = useRef<ReturnType<typeof useReactTable<DataTableV2DataRow>> | null>(null)

  // Extract configuration from uiSchema
  const { title, description, emphasizeHeader, tableConfig } = uiSchema

  const resolvedTableConfig = tableConfig ?? {}

  const canRefresh = coerceBoolean(resolvedTableConfig.canRefresh, false)
  const canSearch = coerceBoolean(resolvedTableConfig.canSearch, false)
  const canExportXlSX = coerceBoolean(resolvedTableConfig.canExportXlSX, false)
  const defaultCompactMode = coerceBoolean(resolvedTableConfig.defaultCompactMode, false)
  const selectionEnabled = coerceBoolean(resolvedTableConfig.selectionEnabled, false)
  const freezePane = resolvedTableConfig.freezePane
  const defaultSort = resolvedTableConfig.defaultSort
  const groupConfig = resolvedTableConfig.groupConfig
  const inlineEditConfig = resolvedTableConfig.inlineEditConfig

  // Content height calculation
  // const hasHeader = !!title && !!tileFilters
  // const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)

  // State
  const [globalFilter, setGlobalFilter] = useState('')
  const [compactMode, setCompactMode] = useState<boolean>(() => defaultCompactMode)
  const [isRegrouping, setIsRegrouping] = useState(false)

  useEffect(() => {
    setCompactMode(defaultCompactMode)
  }, [defaultCompactMode])

  // Determine default expansion state from groupConfig
  const defaultExpanded = groupConfig?.defaultExpanded ?? true

  // Process data
  const rawData = useMemo(() => {
    if (!dataSchema.data || isEmptyObject(dataSchema.data)) return []
    return dataSchema.data as DataTableV2DataRow[]
  }, [dataSchema.data])

  const rawGroups = useMemo(() => {
    if (!dataSchema.groups || isEmptyObject(dataSchema.groups)) return undefined
    return dataSchema.groups as DataTableV2DataRow[]
  }, [dataSchema.groups])

  // Generate columns from schema
  const baseColumns = useMemo(() => getColumnsV2<DataTableV2DataRow>(uiSchema), [uiSchema])

  // Build initial column visibility state from uiSchema
  // Columns with isVisible: false will be hidden but still participate in sorting
  const initialColumnVisibility = useMemo(() => {
    return uiSchema.columns.reduce<Record<string, boolean>>((acc, col) => {
      if (col.isVisible === false) {
        acc[col.id] = false
      }
      return acc
    }, {})
  }, [uiSchema.columns])

  // Transform data for grouping if config is present
  const groupedData = useTableDataGroupingV2({
    data: rawData,
    groups: rawGroups,
    groupConfig,
  })

  // Manage regrouping logic (data, expansion, dropping)
  const {
    localData,
    expandedState,
    handleExpandedChange,
    handleUngroup,
    handleDrop,
    handleDeleteGroup,
    handleUpdateGroup,
    handleResetGroups,
  } = useTableRegroupingV2({
    groupedData,
    defaultExpanded,
    tableRef,
  })

  // Manage drag and drop sensors and events
  const { activeId, sensors, handleDragStart, handleDragEnd, handleDragCancel } = useTableDragDropV2({
    handleDrop,
  })

  // ─── Inline editing ──────────────────────────────────────────────────────────
  const {
    isInlineEditing,
    isFormReady,
    isLoadingFormSchema,
    formPropertyMap,
    control: inlineEditControl,
    formMethods: inlineEditFormMethods,
    onToggleInlineEdit,
    onSaveInlineEdit,
    onCancelInlineEdit,
    isSubmitting: isSubmittingInlineEdit,
    getFieldName,
  } = useInlineEditV2({
    inlineEditConfig,
    localData,
    columns: uiSchema.columns,
  })

  // Wrap columns with inline edit cell renderers when inline editing is active.
  // Only swap to form field cells after the form has been initialized with proper defaults
  // to avoid "uncontrolled to controlled" warnings.
  const columnsWithInlineEdit = useMemo(() => {
    if (!isInlineEditing || !isFormReady || formPropertyMap.size === 0) return baseColumns

    return baseColumns.map((col) => {
      const colId = col.id as string
      const formProperty = formPropertyMap.get(colId)
      if (!formProperty) return col

      return {
        ...col,
        cell: (info: CellContext<DataTableV2DataRow, unknown>) => {
          const row = info.row.original
          // Don't make group rows editable
          if (row.isGroup) {
            return typeof col.cell === 'function' ? col.cell(info) : null
          }
          const fieldName = getFieldName(row.id, formProperty.id)
          // Create a modified field with the encoded field name for inline editing
          const modifiedField = {
            ...formProperty,
            id: fieldName,
          }
          return (
            <div className="min-w-[80px]">
              <FieldRenderer field={modifiedField} control={inlineEditControl} variant="inline" />
            </div>
          )
        },
      }
    })
  }, [baseColumns, isInlineEditing, isFormReady, formPropertyMap, inlineEditControl, getFieldName])

  // Prepare columns with selection and actions if enabled
  const columnsWithSelectionAndActions = useTableColumnsV2(
    columnsWithInlineEdit as ColumnDef<DataTableV2DataRow, unknown>[],
    selectionEnabled,
    uiSchema.rowActions,
    groupConfig
  )

  // Add expander column if grouping is enabled
  const columnsWithGroupExpander = useExpanderColumnV2({
    columns: columnsWithSelectionAndActions,
    isGroupingEnabled: !!groupConfig,
    isRegrouping,
    onUngroup: handleUngroup,
    onDeleteGroup: handleDeleteGroup,
  })

  // Build initial sorting config
  // Use all column IDs from uiSchema (not just visible ones) so sorting works on hidden columns
  const allColumnIds = uiSchema.columns.map((col) => col.id)

  const initialSorting =
    defaultSort && allColumnIds.includes(defaultSort.column)
      ? [
          {
            id: defaultSort.column,
            desc: defaultSort.direction === 'desc',
          },
        ]
      : []

  // Create table instance
  const table = useReactTable({
    data: localData,
    columns: columnsWithGroupExpander,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row: DataTableV2DataRow) => {
      const rowWithSubRows = row as DataTableV2DataRow & { subRows?: DataTableV2DataRow[] }
      if (rowWithSubRows.subRows && Array.isArray(rowWithSubRows.subRows) && rowWithSubRows.subRows.length > 0) {
        return rowWithSubRows.subRows
      }
      return undefined
    },
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: handleExpandedChange,
    globalFilterFn: 'includesString',
    enableRowSelection: selectionEnabled,
    enableHiding: true,
    state: {
      globalFilter,
      expanded: Object.keys(expandedState).length > 0 ? expandedState : defaultExpanded ? true : {},
      columnVisibility: initialColumnVisibility,
    },
    initialState: {
      sorting: initialSorting,
      expanded: defaultExpanded ? true : {},
      columnVisibility: initialColumnVisibility,
    },
    meta: {
      isRegrouping,
      updateGroup: handleUpdateGroup,
      deleteGroup: handleDeleteGroup,
    },
  })

  // Store table reference for use in callbacks
  tableRef.current = table

  // Get filtered rows
  const { rows } = table.getRowModel()
  const shouldVirtualize = rows.length > VIRTUALIZATION_THRESHOLD

  // Set up the virtualizer
  const rowVirtualizer = useVirtualizer<HTMLDivElement, Element>({
    count: rows.length,
    getScrollElement: () => scrollAreaRef.current as HTMLDivElement,
    estimateSize: () => (compactMode ? 50 : 78),
    overscan: 5,
    enabled: shouldVirtualize,
  })

  // Compute column widths
  const computedColWidths = useColumnWidthsV2(table, headerMeasureRefs, cellMeasureRefs)

  // Calculate frozen column offsets
  // Frozen count = (Selection ? 1 : 0) + (Expander/Grouping ? 1 : 0) + (Configured FreezePane ? N : 0)
  const frozenColumnCount = (selectionEnabled ? 1 : 0) + (groupConfig ? 1 : 0) + (freezePane ?? 0)
  const { leafColumns, frozenColOffsetsById } = useFrozenColumnsV2(table, frozenColumnCount, computedColWidths)

  const nrOfColumns = columnsWithGroupExpander.length
  const canRegroup = groupConfig?.canRegroup ?? false

  // Handle grouping save
  const { tileFilterSelections } = useTileFilters()
  const { mutate: submitForm, isPending: isSubmittingGroups } = useSubmitForm(
    groupConfig?.handlerId || '',
    tileFilterSelections
  )

  const onToggleRegrouping = useCallback(() => {
    setIsRegrouping((prev) => !prev)
  }, [])

  const { groupIdCollection = 'dataIds' } = groupConfig || {}
  const onSaveGroups = useCallback(() => {
    const groups = localData
      .filter(
        (item) =>
          item &&
          typeof item === 'object' &&
          'isGroup' in item &&
          (item as Record<string, unknown>).isGroup &&
          'subRows' in item
      )
      .map((item) => {
        const { subRows, isGroup, ...rest } = item as {
          subRows: DataTableV2DataRow[]
          isGroup: boolean
          [key: string]: unknown
        }
        return {
          ...rest,
          [groupIdCollection]: subRows.map((row) => row.id),
        }
      })
    submitForm({ groups })
    setIsRegrouping(false)
  }, [localData, submitForm, groupIdCollection])

  const onCancelGroups = useCallback(() => {
    handleResetGroups()
    setIsRegrouping(false)
  }, [handleResetGroups])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      modifiers={[restrictToVerticalAxis]}
    >
      <div className="flex h-full flex-col">
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer className="gap-1">
            {isInlineEditing ? (
              /* When inline editing is active, only show save/cancel */
              <InlineEditActionsV2
                isInlineEditing={isInlineEditing}
                isInlineEditEnabled
                onToggleInlineEdit={onToggleInlineEdit}
                onSaveInlineEdit={onSaveInlineEdit}
                onCancelInlineEdit={onCancelInlineEdit}
                isSubmitting={isSubmittingInlineEdit}
                isLoadingFormSchema={isLoadingFormSchema}
              />
            ) : (
              <>
                {/* Inline Edit Actions (toggle button) — hidden during regrouping */}
                {!isRegrouping && (
                  <InlineEditActionsV2
                    isInlineEditing={isInlineEditing}
                    isInlineEditEnabled={!!inlineEditConfig?.isInlineEditEnabled}
                    onToggleInlineEdit={onToggleInlineEdit}
                    onSaveInlineEdit={onSaveInlineEdit}
                    onCancelInlineEdit={onCancelInlineEdit}
                    isSubmitting={isSubmittingInlineEdit}
                    isLoadingFormSchema={isLoadingFormSchema}
                  />
                )}

                {/* Grouping Actions */}
                <GroupingActionsV2
                  isRegrouping={isRegrouping}
                  canRegroup={canRegroup}
                  onToggleRegrouping={onToggleRegrouping}
                  onSaveGroups={onSaveGroups}
                  onCancelGroups={onCancelGroups}
                  isSubmittingGroups={isSubmittingGroups}
                />

                {/* Table Actions Placeholder */}
                {uiSchema.tableActions && uiSchema.tableActions.length > 0 && !isRegrouping && (
                  <TableActionsV2
                    actions={uiSchema.tableActions}
                    dataSchema={dataSchema}
                    localData={localData}
                  />
                )}

                {/* Export to Excel Button */}
                {canExportXlSX && !isRegrouping && (
                  <ExportButtonV2 table={table} data={localData} title={title} selectionRequired={selectionEnabled} />
                )}

                {canRefresh && reloadHandler && !isRegrouping && (
                  <TooltipButton tooltip="Refresh data">
                    <Button size="icon" variant="ghost" className="size-8 rounded-full" onClick={reloadHandler}>
                      <IconV2RotateClockwiseOutline className="size-6" />
                    </Button>
                  </TooltipButton>
                )}

                {canSearch && <SearchInput value={globalFilter} onChange={setGlobalFilter} />}

                {tileFilters}
              </>
            )}
          </ComponentActionsContainer>
        </ComponentHeader>

        {/* <div style={{ height: contentHeight }} className="relative w-full pt-5"> */}
        <div className="relative min-h-0 w-full flex-1 pt-5">
          <CompactViewToggleV2 compactMode={compactMode} setCompactMode={setCompactMode} />

          <InlineEditFormWrapper isInlineEditing={isInlineEditing} formMethods={inlineEditFormMethods}>
            <div
              ref={scrollAreaRef}
              className="h-auto max-h-full w-full overflow-auto rounded-md border [&>div]:overflow-visible"
            >
              <div
                className={clsx('pointer-events-none absolute inset-0 z-10 rounded-md bg-secondary/5 bg-blend-screen', {
                  hidden: !isInlineEditing,
                })}
              />
              <Table className="min-w-max table-auto border-separate border-spacing-0">
                <colgroup>
                  {table.getVisibleLeafColumns().map((column) => (
                    <col
                      key={column.id}
                      style={{
                        minWidth: computedColWidths[column.id] ? `${computedColWidths[column.id]}px` : undefined,
                        width: column.columnDef.size ? `${column.columnDef.size}px` : undefined,
                      }}
                    />
                  ))}
                </colgroup>

                <DataTableHeaderV2
                  table={table}
                  frozenColOffsetsById={frozenColOffsetsById}
                  leafColumns={leafColumns}
                  freezePane={frozenColumnCount}
                  headerMeasureRefs={headerMeasureRefs}
                />

                <DataTableBodyV2
                  tableBodyRef={tableBodyRef}
                  isRefetching={isRefetching}
                  nrOfColumns={nrOfColumns}
                  rows={rows}
                  shouldVirtualize={shouldVirtualize}
                  rowVirtualizer={rowVirtualizer}
                  frozenColOffsetsById={frozenColOffsetsById}
                  leafColumns={leafColumns}
                  freezePane={frozenColumnCount}
                  compactMode={compactMode}
                  cellMeasureRefs={cellMeasureRefs}
                  isRegrouping={isRegrouping}
                  activeDragId={activeId}
                />
              </Table>
            </div>
          </InlineEditFormWrapper>
        </div>
      </div>

      <TableDragOverlayV2
        activeId={activeId}
        table={table}
        computedColWidths={computedColWidths}
        frozenColOffsetsById={frozenColOffsetsById}
        leafColumns={leafColumns}
        frozenColumnCount={frozenColumnCount}
        compactMode={compactMode}
        isRegrouping={isRegrouping}
        cellMeasureRefs={overlayCellMeasureRefs}
      />
    </DndContext>
  )
}
